/**
 * @file 视图规约器（viewReducer）—— 联机模式的客户端状态机。
 *
 * 服务端只在 `welcome` / `snap` 时下发全量 `SeatView`，日常走 `ev` 事件增量。
 * 本模块把 `WireEvent[]` 应用到本地 `ClientViewState` 上（纯函数、无副作用），
 * 并顺带还原响应窗口（claim_window_opened / claim_options）——这两个字段在
 * `SeatView` 里不存在（投影层 `actionHint` 恒为 null），必须由客户端从事件还原。
 *
 * ## 信息隔离
 * 只消费公开事件载荷与 `SeatView`；他家手牌只维护 `handCount`（事件里也只有计数）。
 * 发现 `seq` 缺口（`action_rejected.serverSeq > view.seq`）时置 `needsResync`。
 */

import type {
  ClaimActionType,
  GamePhase,
  MeldType,
  SeatIndex,
  SeatView,
  TileId,
  TileKind,
} from '@majiang/core';
import { idsOfKind, kindOfTileId } from '@majiang/core';
import type { WireEvent } from '@majiang/protocol';
import type { ClaimWindowState, ClientViewState, MyClaimState } from './types';

/** 初始状态（对局未开始 → view 为 null）。 */
export function createClientState(view: SeatView | null): ClientViewState {
  return { view, claimWindow: null, myClaim: null, selfTingKinds: null, needsResync: false };
}

/** 用全量快照整体替换（welcome / snap）。 */
export function applySnapshot(state: ClientViewState, view: SeatView): ClientViewState {
  return { ...state, view, claimWindow: null, myClaim: null, needsResync: false };
}

/** 应用一批事件。发现缺口即停（等 snap）。 */
export function applyEvents(state: ClientViewState, events: readonly WireEvent[]): ClientViewState {
  let s = state;
  for (const ev of events) {
    s = applyEvent(s, ev);
    if (s.needsResync) break;
  }
  return s;
}

/* ─────────────────────────── 逐事件应用 ─────────────────────────── */

function applyEvent(s: ClientViewState, ev: WireEvent): ClientViewState {
  const view = s.view;
  if (view === null) return s; // 尚无基线：等 snap（OnlineSession 会主动 resync）
  switch (ev.type) {
    case 'dealt': {
      const { handCounts, wallRemaining } = ev.payload;
      return withOpponents(s, (o) => ({ ...o, handCount: handCounts[o.seat] ?? o.handCount }), { wallRemaining });
    }
    case 'dealt_self': {
      const hand = ev.payload.hand;
      return withSelf(s, { hand });
    }
    case 'swap_done':
      return withView(s, { swapDirection: ev.payload.direction });
    case 'swap_result_self':
      return withSelf(s, { hand: [...ev.payload.received] });
    case 'void_declared': {
      const { seat, suit } = ev.payload;
      if (seat === view.viewerSeat) return withSelf(s, { voidSuit: suit });
      return withOpponent(s, seat, { voidSuit: suit });
    }
    case 'void_done': {
      const suits = ev.payload.voidSuits;
      return withOpponents(s, (o) => ({ ...o, voidSuit: suits[o.seat] ?? o.voidSuit }), {
        self: { voidSuit: suits[view.viewerSeat] ?? view.self.voidSuit },
      });
    }
    case 'drawn': {
      const { seat, wallRemaining } = ev.payload;
      if (seat === view.viewerSeat) return withView(s, { wallRemaining });
      // 他家摸牌：公开计数 +1（牌面不可见，仅张数）
      return withOpponent(s, seat, { handCount: (oppHandCount(view, seat) ?? 0) + 1 }, { wallRemaining });
    }
    case 'drawn_self': {
      const { seat, tileId, wallRemaining } = ev.payload;
      if (seat !== view.viewerSeat) return s;
      const hand = [...view.self.hand, tileId].sort((a, b) => a - b);
      return withSelf(s, { hand }, { wallRemaining });
    }
    case 'discarded': {
      const { seat, tileId } = ev.payload;
      if (seat === view.viewerSeat) {
        const hand = view.self.hand.filter((id) => id !== tileId);
        return withSelf(s, { hand, discards: [...view.self.discards, tileId] }, {
          lastDiscard: { tileId, seat, atTurn: view.turnCount, claimed: false },
        });
      }
      return withOpponent(s, seat, {
        discards: [...oppDiscards(view, seat), tileId],
        handCount: Math.max(0, (oppHandCount(view, seat) ?? 0) - 1),
      }, {
        lastDiscard: { tileId, seat, atTurn: view.turnCount, claimed: false },
      });
    }
    case 'claim_window_opened': {
      const { fromSeat, tileId, trigger, deadlineAt } = ev.payload;
      const claimWindow: ClaimWindowState = { fromSeat, tileId, trigger, deadlineAt };
      return { ...s, claimWindow };
    }
    case 'claim_options': {
      const { seat, tileId, fromSeat, trigger, actions, gangKinds, deadlineAt } = ev.payload;
      if (seat !== view.viewerSeat) return s;
      const myClaim: MyClaimState = { tileId, fromSeat, trigger, actions, gangKinds, deadlineAt };
      return { ...s, myClaim };
    }
    case 'peng_made': {
      const { seat, fromSeat, kind, tileId } = ev.payload;
      let next: ClientViewState;
      if (seat === view.viewerSeat) {
        const { removed, pair } = takeTwoOfKind(view.self.hand, kind);
        const meld = { type: 'peng' as const, kind, tiles: [...pair, tileId].sort((a, b) => a - b), fromSeat, claimedTileId: tileId, atTurn: view.turnCount };
        next = withSelf(s, { hand: removed, melds: [...view.self.melds, meld] });
      } else {
        const meld = { type: 'peng' as const, kind, tiles: synthMeldTiles(kind, tileId, 3), fromSeat, claimedTileId: tileId, atTurn: view.turnCount };
        next = withOpponent(s, seat, { handCount: (oppHandCount(view, seat) ?? 0) - 2, melds: [...oppMelds(view, seat), meld] });
      }
      // 被碰的牌从点炮者弃牌河移除（公开信息）
      next = removeFromRiver(next, fromSeat, tileId);
      return clearClaim(next);
    }
    case 'gang_made': {
      const { seat, meldType, kind, fromSeat, tileId } = ev.payload;
      let next: ClientViewState;
      if (seat === view.viewerSeat) {
        // 自家：暗杠走 gang_made_self（scope: seat 完整牌面）；此处只可能 gang_bu / gang_ming
        if (meldType === 'gang_an' || kind === null) return s;
        if (meldType === 'gang_bu') {
          // 巴杠：碰刻子升级为补杠；第 4 张从手牌移入副露
          const removed = removeOneOfKind(view.self.hand, kind);
          next = withSelf(s, { hand: removed, melds: upgradeBuGang(view.self.melds, kind) });
        } else {
          // 明杠
          if (tileId === null) return s;
          const { removed, three } = takeThreeOfKind(view.self.hand, kind);
          const meld = { type: 'gang_ming' as const, kind, tiles: [...three, tileId].sort((a, b) => a - b), fromSeat, claimedTileId: tileId, atTurn: view.turnCount };
          next = withSelf(s, { hand: removed, melds: [...view.self.melds, meld] });
        }
      } else if (meldType === 'gang_bu') {
        // 他家巴杠：碰刻子升级为补杠，手牌 -1
        if (kind === null) return s;
        next = withOpponent(s, seat, { handCount: (oppHandCount(view, seat) ?? 0) - 1, melds: upgradeBuGang(oppMelds(view, seat), kind) });
      } else if (meldType === 'gang_an') {
        // 他家暗杠：D-14 只公开「谁暗杠了」，牌面隐藏为 4 张牌背
        const anMeld = { type: 'gang_an' as const, kind: null, tiles: [], fromSeat: null, claimedTileId: null, atTurn: view.turnCount };
        next = withOpponent(s, seat, { handCount: (oppHandCount(view, seat) ?? 0) - 4, melds: [...oppMelds(view, seat), anMeld] });
      } else {
        // 他家明杠：牌面公开，牌实例 id 由 kind 合成（事件不带他家手牌 id）
        if (kind === null) return s;
        const meld = { type: meldType, kind, tiles: synthMeldTiles(kind, tileId, 4), fromSeat, claimedTileId: tileId, atTurn: view.turnCount };
        next = withOpponent(s, seat, { handCount: (oppHandCount(view, seat) ?? 0) - 3, melds: [...oppMelds(view, seat), meld] });
      }
      if (tileId !== null && fromSeat !== null && seat !== fromSeat) {
        next = removeFromRiver(next, fromSeat, tileId);
      }
      // 明杠后轮转到杠家出牌，但 core 不发 turn_changed（game.ts doMingGang）——
      // 客户端必须从 gang_made 事件自行推导，否则会把出牌权误判给点炮者。
      if (meldType === 'gang_ming') {
        next = withView(next, { turn: seat });
      }
      return clearClaim(next);
    }
    case 'gang_made_self': {
      if (ev.payload.seat !== view.viewerSeat) return s;
      const { kind, tiles } = ev.payload;
      const hand = view.self.hand.filter((id) => !tiles.includes(id));
      const meld = { type: 'gang_an' as const, kind, tiles: [...tiles].sort((a, b) => a - b), fromSeat: null, claimedTileId: null, atTurn: view.turnCount };
      return withSelf(s, { hand, melds: [...view.self.melds, meld] });
    }
    case 'won': {
      const { seat, winningTile, fanTags, rawFan, cappedFan, winnersInOrder, revealedTiles } = ev.payload;
      const record = { seat, kind: ev.payload.kind, fromSeat: ev.payload.fromSeat, winningTile, fanTags, rawFan, cappedFan, atTurn: view.turnCount };
      let next: ClientViewState;
      if (seat === view.viewerSeat) {
        next = withSelf(s, {
          hasWon: true,
          winRecords: [...view.self.winRecords, record],
          hand: revealedTiles !== null ? [...revealedTiles] : view.self.hand,
        });
      } else {
        next = withOpponent(s, seat, {
          hasWon: true,
          winRecords: [...oppWinRecords(view, seat), record],
        });
      }
      return withView(next, { winnersInOrder });
    }
    case 'passed':
      // 仅影响窗口节奏；窗口关闭由 turn_changed / peng_made 等统一清理
      return s;
    case 'turn_changed': {
      const { seat, phase, deadlineAt } = ev.payload;
      const v: SeatView = {
        ...view,
        turn: seat,
        phase: (phase ?? view.phase) as GamePhase,
        deadlineAt: deadlineAt ?? view.deadlineAt,
      };
      return { ...s, view: v, claimWindow: null, myClaim: null };
    }
    case 'ting_changed': {
      const { seat, isTing, candidates } = ev.payload;
      if (seat !== view.viewerSeat) return s;
      return {
        ...withSelf(s, { isTing }),
        selfTingKinds: candidates ?? null,
      };
    }
    case 'settled': {
      const { netScores, dajiaoSeats, flowerPigSeats, gangRefunds, revealedHands } = ev.payload;
      const selfHand = revealedHands[view.viewerSeat];
      const self = selfHand !== undefined ? { ...view.self, hand: [...selfHand] } : view.self;
      const v: SeatView = {
        ...view,
        phase: 'settlement',
        scores: netScores,
        settlement: { transfers: ev.payload.transfers, netScores, dajiaoSeats, flowerPigSeats, gangRefunds },
        self,
      };
      return { ...s, view: v, claimWindow: null, myClaim: null };
    }
    case 'game_finished': {
      const v: SeatView = { ...view, phase: 'finished', scores: ev.payload.finalScores };
      return { ...s, view: v, claimWindow: null, myClaim: null };
    }
    case 'action_rejected': {
      const { serverSeq } = ev.payload;
      if (serverSeq > view.seq) return { ...s, needsResync: true };
      return s;
    }
    default:
      return s;
  }
}

/* ─────────────────────────── 不可变更新助手 ─────────────────────────── */

type SelfPatch = Partial<Pick<SeatView['self'], 'hand' | 'melds' | 'discards' | 'voidSuit' | 'hasWon' | 'winRecords' | 'isTing'>>;
type OppPatch = Partial<Pick<SeatView['opponents'][number], 'handCount' | 'melds' | 'discards' | 'voidSuit' | 'hasWon' | 'winRecords'>>;
type ViewPatch = Partial<Pick<SeatView, 'wallRemaining' | 'lastDiscard' | 'turn' | 'phase' | 'deadlineAt' | 'winnersInOrder' | 'scores' | 'settlement' | 'self' | 'opponents' | 'swapDirection'>>;

function withView(s: ClientViewState, patch: ViewPatch): ClientViewState {
  const view = s.view;
  if (view === null) return s;
  return { ...s, view: { ...view, ...patch } };
}

function withSelf(s: ClientViewState, selfPatch: SelfPatch, viewPatch?: ViewPatch): ClientViewState {
  const view = s.view;
  if (view === null) return s;
  return { ...s, view: { ...view, self: { ...view.self, ...selfPatch }, ...viewPatch } };
}

function withOpponent(s: ClientViewState, seat: SeatIndex, patch: OppPatch, viewPatch?: ViewPatch): ClientViewState {
  const view = s.view;
  if (view === null) return s;
  const opponents = view.opponents.map((o) => (o.seat === seat ? { ...o, ...patch } : o));
  return { ...s, view: { ...view, opponents, ...viewPatch } };
}

/** 批量更新所有对手（适合 dealt / void_done）。 */
function withOpponents(s: ClientViewState, patchFn: (o: SeatView['opponents'][number]) => SeatView['opponents'][number], viewPatch?: { wallRemaining?: number; self?: SelfPatch }): ClientViewState {
  const view = s.view;
  if (view === null) return s;
  const opponents = view.opponents.map(patchFn);
  const self = viewPatch?.self !== undefined ? { ...view.self, ...viewPatch.self } : view.self;
  return { ...s, view: { ...view, opponents, self, wallRemaining: viewPatch?.wallRemaining ?? view.wallRemaining } };
}

function oppDiscards(view: SeatView, seat: SeatIndex): readonly TileId[] {
  return view.opponents.find((o) => o.seat === seat)?.discards ?? [];
}
function oppMelds(view: SeatView, seat: SeatIndex): SeatView['opponents'][number]['melds'] {
  return view.opponents.find((o) => o.seat === seat)?.melds ?? [];
}
function oppWinRecords(view: SeatView, seat: SeatIndex): SeatView['opponents'][number]['winRecords'] {
  return view.opponents.find((o) => o.seat === seat)?.winRecords ?? [];
}
function oppHandCount(view: SeatView, seat: SeatIndex): number | undefined {
  return view.opponents.find((o) => o.seat === seat)?.handCount;
}

/** 从某座位弃牌河移除一张牌（被碰/杠走）。 */
function removeFromRiver(s: ClientViewState, seat: SeatIndex, tileId: TileId): ClientViewState {
  const view = s.view;
  if (view === null) return s;
  if (seat === view.viewerSeat) {
    return withSelf(s, { discards: view.self.discards.filter((id) => id !== tileId) });
  }
  return withOpponent(s, seat, { discards: oppDiscards(view, seat).filter((id) => id !== tileId) });
}

/** 把手牌中该牌面取走 2 张（碰），返回剩余手牌与被取走的牌。 */
function takeTwoOfKind(hand: readonly TileId[], kind: TileKind): { readonly removed: readonly TileId[]; readonly pair: readonly TileId[] } {
  const pair: TileId[] = [];
  const removed: TileId[] = [];
  for (const id of hand) {
    if (pair.length < 2 && kindOfTileId(id) === kind) pair.push(id);
    else removed.push(id);
  }
  return { removed, pair };
}

/** 取走 3 张（明杠）。 */
function takeThreeOfKind(hand: readonly TileId[], kind: TileKind): { readonly removed: readonly TileId[]; readonly three: readonly TileId[] } {
  const three: TileId[] = [];
  const removed: TileId[] = [];
  for (const id of hand) {
    if (three.length < 3 && kindOfTileId(id) === kind) three.push(id);
    else removed.push(id);
  }
  return { removed, three };
}

/**
 * 碰 → 补杠（巴杠）升级：把该牌面的 peng 副露替换为 gang_bu，
 * 并补全 4 张牌面（第 4 张的实例 id 对他家不可见，按公开映射合成）。
 */
function upgradeBuGang<M extends { readonly type: MeldType; readonly kind: TileKind | null; readonly tiles: readonly TileId[]; readonly claimedTileId: TileId | null }>(melds: readonly M[], kind: TileKind): M[] {
  return melds.map((m) =>
    m.type === 'peng' && m.kind === kind
      ? ({ ...m, type: 'gang_bu' as MeldType, tiles: synthMeldTiles(kind, null, 4), claimedTileId: null } as M)
      : m,
  );
}

/** 从手牌移除一张指定牌面（巴杠用）。 */
function removeOneOfKind(hand: readonly TileId[], kind: TileKind): readonly TileId[] {
  const out: TileId[] = [];
  let removed = false;
  for (const id of hand) {
    if (!removed && kindOfTileId(id) === kind) {
      removed = true;
      continue;
    }
    out.push(id);
  }
  return out;
}

/**
 * 合成副露的牌实例 id 列表。
 * 用于他家副露：事件只公开 `kind` / `claimedTileId`，不含他家手牌 id，
 * 渲染只需要「张数 + 牌面 + 被claim的那张」。id 用公开映射合成即可。
 */
function synthMeldTiles(kind: TileKind, claimedTileId: TileId | null, count: number): TileId[] {
  const all = idsOfKind(kind);
  const out: TileId[] = [];
  for (const id of all) {
    if (out.length >= count) break;
    if (id === claimedTileId) continue;
    out.push(id);
  }
  while (out.length < count && claimedTileId !== null) out.push(claimedTileId);
  return out.sort((a, b) => a - b);
}

/** 清理响应窗口（碰/杠/胡/轮转后窗口必已关闭）。 */
function clearClaim(s: ClientViewState): ClientViewState {
  return { ...s, claimWindow: null, myClaim: null };
}

/* ─────────────────────────── 供测试/自检暴露的内部 ─────────────────────────── */

/** 判断某个 ClaimActionType 是否在响应选项中。 */
export function hasClaim(actions: readonly ClaimActionType[], type: ClaimActionType): boolean {
  return actions.includes(type);
}
